import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getSettings,
  updateSettings,
  testCJConnection,
  clearCJSettings,
} from "../lib/api";
import { CheckCircle2, XCircle, Plug, Eye, EyeOff, Loader2, Trash2 } from "lucide-react";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [email, setEmail] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = () => getSettings().then(setSettings).catch(() => setSettings({}));

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error("API key is required");
      return;
    }
    setSaving(true);
    try {
      await updateSettings({
        cj_api_key: apiKey.trim(),
        cj_api_email: email.trim() || undefined,
      });
      toast.success("Settings saved");
      setApiKey("");
      setEmail("");
      setTestResult(null);
      load();
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testCJConnection();
      setTestResult(res);
      if (res.ok) toast.success("CJ Dropshipping connected");
      else toast.error("Connection failed", { description: res.message });
    } catch (e) {
      setTestResult({ ok: false, message: e?.message || "Error" });
    } finally {
      setTesting(false);
    }
  };

  const handleClear = async () => {
    if (!confirm("Remove CJ Dropshipping API credentials?")) return;
    try {
      await clearCJSettings();
      toast.success("CJ credentials removed");
      setTestResult(null);
      load();
    } catch (e) {
      toast.error("Failed to clear");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="settings-page">
      <div className="mb-10">
        <div className="micro-label mb-3">INTEGRATIONS</div>
        <h1 className="text-5xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>
          Settings
        </h1>
        <p className="mt-3 text-zinc-600 max-w-2xl">
          Connect your CJ Dropshipping merchant account to enable real-time product catalogue
          lookups and shipping quotes to Ontario. Research still works without it using public
          trend signals.
        </p>
      </div>

      <div className="border border-zinc-200">
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-zinc-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 klein-bg flex items-center justify-center">
              <Plug size={22} strokeWidth={2.5} color="#fff" />
            </div>
            <div>
              <div
                className="text-2xl font-black tracking-tight"
                style={{ fontFamily: "Outfit" }}
              >
                CJ Dropshipping API
              </div>
              <div className="micro-label mt-1">
                {settings?.cj_configured ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-600">
                    <CheckCircle2 size={12} strokeWidth={2.5} /> CONNECTED
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-zinc-500">
                    <XCircle size={12} strokeWidth={2.5} /> NOT CONFIGURED
                  </span>
                )}
              </div>
            </div>
          </div>
          {settings?.cj_configured && (
            <div className="text-right hidden sm:block">
              <div className="micro-label">Current Key</div>
              <div className="font-mono text-sm mt-1" data-testid="current-cj-key">
                {settings.cj_api_key_masked}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 md:p-8 space-y-6">
          <div>
            <label className="micro-label block mb-2">CJ Merchant Email (optional)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="cj-email-input"
              className="w-full px-4 py-3 border border-zinc-300 font-mono text-sm focus:outline-none focus:border-[#002FA7]"
            />
          </div>

          <div>
            <label className="micro-label block mb-2">CJ API Key</label>
            <div className="relative">
              <input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="CJUserNum@api@xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                data-testid="cj-key-input"
                className="w-full px-4 py-3 pr-12 border border-zinc-300 font-mono text-sm focus:outline-none focus:border-[#002FA7]"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
                aria-label="Toggle visibility"
              >
                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
              Generate in CJ Dropshipping &rarr; My CJ &rarr; Authorization &rarr; API. Stored
              server-side only.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <button
              onClick={handleSave}
              disabled={saving || !apiKey.trim()}
              data-testid="save-settings-btn"
              className="btn-klein text-xs"
            >
              {saving ? "SAVING..." : "SAVE KEY"}
            </button>
            <button
              onClick={handleTest}
              disabled={testing || !settings?.cj_configured}
              data-testid="test-cj-btn"
              className="btn-outline-dark text-xs inline-flex items-center gap-2"
            >
              {testing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Plug size={14} strokeWidth={2.5} />
              )}
              TEST CONNECTION
            </button>
            {settings?.cj_configured && (
              <button
                onClick={handleClear}
                data-testid="clear-cj-btn"
                className="text-xs uppercase tracking-widest font-bold text-red-600 hover:text-red-800 inline-flex items-center gap-2 ml-auto"
              >
                <Trash2 size={14} strokeWidth={2.5} /> Remove
              </button>
            )}
          </div>

          {testResult && (
            <div
              className={`p-4 border-l-4 ${
                testResult.ok
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-red-500 bg-red-50"
              }`}
              data-testid="cj-test-result"
            >
              <div className="flex items-center gap-2 font-bold text-sm">
                {testResult.ok ? (
                  <>
                    <CheckCircle2 size={16} className="text-emerald-600" strokeWidth={2.5} />
                    Connected
                  </>
                ) : (
                  <>
                    <XCircle size={16} className="text-red-600" strokeWidth={2.5} />
                    Failed
                  </>
                )}
              </div>
              <div className="text-xs text-zinc-700 mt-1 font-mono">{testResult.message}</div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 border border-zinc-200 p-6 md:p-8 bg-zinc-50">
        <div className="micro-label mb-3">HOW IT'S USED</div>
        <ul className="text-sm text-zinc-700 space-y-2 leading-relaxed">
          <li>
            <span className="font-bold">Research pipeline</span> — queries your real CJ catalogue
            instead of public scraping when connected.
          </li>
          <li>
            <span className="font-bold">Shipping accuracy</span> — live freight quotes to Ontario
            postal codes replace estimates.
          </li>
          <li>
            <span className="font-bold">Fallback</span> — if the API is unreachable, the pipeline
            transparently falls back to scraping + Claude knowledge.
          </li>
        </ul>
      </div>
    </div>
  );
}
